import React, { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { Button } from '../../components/common/Button'
import axios from 'axios'
import { useTheme } from '../../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

export const QuarterlyPage: React.FC = () => {
  const { colors } = useTheme()
  const [selectedQuarter, setSelectedQuarter] = useState<string>('')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['analytics-quarterly', selectedQuarter],
    queryFn: async () => {
      const params = selectedQuarter ? `?quarter=${selectedQuarter}` : ''
      const { data } = await axios.get(`${API_BASE_URL}/markets/analytics/quarterly-report${params}`)
      return data
    },
    enabled: true,
  })

  const generatePdfMutation = useMutation({
    mutationFn: async () => {
      const parts = selectedQuarter ? selectedQuarter.split('-') : ['Q4', '2024']
      const quarter = parseInt(parts[0].replace('Q', ''))
      const year = parseInt(parts[1])
      const response = await axios.get(`${API_BASE_URL}/reports/export/pdf/Q${quarter}-${year}`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.download = `quarterly-report-${selectedQuarter || 'latest'}.pdf`
      link.click()
    }
  })

  const quarters = [
    'Q4-2024', 'Q3-2024', 'Q2-2024', 'Q1-2024',
    'Q4-2023', 'Q3-2023', 'Q2-2023', 'Q1-2023',
  ]

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #4A90E2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading quarterly report...</p>
          </div>
        </main>
      </div>
    )
  }

  const report = data?.report || data || {}

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
              Quarterly Business Report
            </h1>
            <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
              Comprehensive quarterly analysis based on Expandly database
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
              style={{
                padding: '10px 16px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: colors.cardBackground,
                color: colors.textPrimary,
              }}
            >
              <option value="">Current Quarter</option>
              {quarters.map(q => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
            <Button onClick={() => refetch()}>Refresh</Button>
            <Button
              variant="outline"
              onClick={() => generatePdfMutation.mutate()}
              disabled={generatePdfMutation.isPending}
            >
              {generatePdfMutation.isPending ? 'Generating...' : 'Export PDF'}
            </Button>
          </div>
        </div>

        {error ? (
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444' }}>Error loading report: {(error as Error).message}</p>
          </Card>
        ) : (
          <>
            {/* Executive Summary */}
            <Card style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '20px', marginBottom: '16px', color: colors.textPrimary }}>Executive Summary</h2>
              <p style={{ color: colors.textSecondary, lineHeight: 1.6 }}>
                {report.summary || 'Quarterly business performance analysis for Expandly platform.'}
              </p>
            </Card>

            {/* Key Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '24px' }}>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Orders</p>
                  <p style={{ fontSize: '32px', fontWeight: 700, color: colors.primary }}>
                    {(report.total_orders || 0).toLocaleString()}
                  </p>
                  <p style={{ color: report.orders_growth >= 0 ? '#50C878' : '#EF4444', fontSize: '14px' }}>
                    {report.orders_growth >= 0 ? '+' : ''}{(report.orders_growth || 0).toFixed(1)}% vs prev
                  </p>
                </div>
              </Card>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Revenue</p>
                  <p style={{ fontSize: '32px', fontWeight: 700, color: '#50C878' }}>
                    ${(report.total_revenue || 0).toLocaleString()}
                  </p>
                  <p style={{ color: report.revenue_growth >= 0 ? '#50C878' : '#EF4444', fontSize: '14px' }}>
                    {report.revenue_growth >= 0 ? '+' : ''}{(report.revenue_growth || 0).toFixed(1)}% vs prev
                  </p>
                </div>
              </Card>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Active Sellers</p>
                  <p style={{ fontSize: '32px', fontWeight: 700, color: '#7B68EE' }}>
                    {(report.active_sellers || 0).toLocaleString()}
                  </p>
                  <p style={{ color: '#50C878', fontSize: '14px' }}>
                    +{report.new_sellers || 0} new this quarter
                  </p>
                </div>
              </Card>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Avg Order Value</p>
                  <p style={{ fontSize: '32px', fontWeight: 700, color: '#FF6B35' }}>
                    ${(report.avg_order_value || 0).toFixed(2)}
                  </p>
                  <p style={{ color: report.aov_change >= 0 ? '#50C878' : '#EF4444', fontSize: '14px' }}>
                    {report.aov_change >= 0 ? '+' : ''}{(report.aov_change || 0).toFixed(1)}% vs prev
                  </p>
                </div>
              </Card>
            </div>

            {/* Performance by Region */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <Card>
                <h2 style={{ fontSize: '18px', marginBottom: '16px', color: colors.textPrimary }}>Top Markets by Revenue</h2>
                {(report.top_markets || []).map((market: { country: string; revenue: number; orders: number }, idx: number) => (
                  <div key={market.country} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: idx < (report.top_markets?.length || 0) - 1 ? `1px solid ${colors.border}` : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: colors.primary + '20',
                        color: colors.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: '14px',
                      }}>{idx + 1}</span>
                      <span style={{ fontWeight: 500, color: colors.textPrimary }}>{market.country}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontWeight: 600, color: colors.textPrimary }}>${market.revenue.toLocaleString()}</p>
                      <p style={{ fontSize: '12px', color: colors.textSecondary }}>{market.orders} orders</p>
                    </div>
                  </div>
                ))}
              </Card>
              <Card>
                <h2 style={{ fontSize: '18px', marginBottom: '16px', color: colors.textPrimary }}>Top Product Categories</h2>
                {(report.top_categories || []).map((cat: { name: string; revenue: number; percentage: number }, idx: number) => (
                  <div key={cat.name} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: idx < (report.top_categories?.length || 0) - 1 ? `1px solid ${colors.border}` : 'none',
                  }}>
                    <span style={{ fontWeight: 500, color: colors.textPrimary }}>{cat.name}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{
                        width: '100px',
                        height: '8px',
                        backgroundColor: colors.border,
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${cat.percentage}%`,
                          height: '100%',
                          backgroundColor: colors.primary,
                          borderRadius: '4px',
                        }} />
                      </div>
                      <span style={{ fontWeight: 600, color: colors.textPrimary, minWidth: '80px', textAlign: 'right' }}>
                        ${cat.revenue.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </Card>
            </div>

            {/* AI Insights */}
            {report.ai_insights && (
              <Card style={{
                background: `linear-gradient(135deg, ${colors.cardBackground}, #7B68EE10)`,
                border: '1px solid #7B68EE40',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '20px' }}>🤖</span>
                  <h2 style={{ fontSize: '18px', color: colors.textPrimary }}>AI-Generated Insights</h2>
                </div>
                <div style={{ color: colors.textSecondary, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {report.ai_insights}
                </div>
              </Card>
            )}
          </>
        )}
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
